import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['lineWithTag', 'floater', 'prevs', 'nexts', 'previewSpan', 'tagValueInput', 'filledByCandidateInput'];

  static values = {
    index: Number
  };

  static classes = ['displayNone', 'active', 'underline', 'strikethrough'];

  initialize() {
    // hide previous button on first floater
    if (this.hasPrevsTarget) {
      this.prevsTargets[0].classList.add(this.displayNoneClass);
    }
    // hide next button on last floater
    if (this.hasNextsTarget) {
      this.nextsTargets[this.nextsTargets.length - 1].classList.add(this.displayNoneClass);
    }
    this.createAndFillPreviewSpans();
  }

  indexValueChanged() {
    this.showField();
  }

  // ACTIONS

  activateLine(e) {
    if (e.target.closest('.js-floater')) return; // do not activate line on floater div click

    const target = e.target.closest('.js-has-tag');
    if (target.classList.contains(this.activeClass)) {
      this.hideAllFields();
    } else {
      /* in case user clicks multiple times on same Line the indexValueChanged callback
         would not trigger because index Value would be the same.
         So we trigger the showField method manually here
      */
      const newIndexValue = Array.from(this.lineWithTagTargets).indexOf(target);
      if (newIndexValue === this.indexValue) {
        this.showField();
      } else {
        this.indexValue = newIndexValue;
      }
    }
  }

  previousField() {
    this.indexValue -= 1;
  }

  nextField() {
    this.indexValue += 1;
  }

  closeField() {
    this.hideAllFields();
  }

  focusTagValue(e) {
    const i = Array.from(this.tagValueInputTargets).indexOf(e.target);
    const previewSpan = this.previewSpanTargets[i];
    if (!previewSpan) return;
    const tributeSpan = previewSpan.nextSibling;
    previewSpan.classList.add(this.underlineClass);
    if (tributeSpan) tributeSpan.classList.add(this.underlineClass, this.strikethroughClass);
  }

  focusoutTagValue(e) {
    const i = Array.from(this.tagValueInputTargets).indexOf(e.target);
    const previewSpan = this.previewSpanTargets[i];
    if (!previewSpan) return;
    const tributeSpan = previewSpan.nextSibling;
    previewSpan.classList.remove(this.underlineClass);
    if (tributeSpan) tributeSpan.classList.remove(this.underlineClass, this.strikethroughClass);
  }

  changeTagValue(e) {
    /* The same tag can appear several times in the contract, and the form renders one input per
       occurrence — all bound to the same merge field row. Keep every occurrence in step with the
       one being typed into, so the document reads correctly and every submitted copy agrees. */
    const inputs = Array.from(this.tagValueInputTargets);
    const tagKey = e.target.dataset.tagKey;

    inputs.forEach((input, i) => {
      if (input !== e.target) {
        if (!tagKey || input.dataset.tagKey !== tagKey) return;
        input.value = e.target.value;
      }
      this.renderPreviewFor(i, e.target.value.trim());
    });
  }

  renderPreviewFor(index, inputValue) {
    const previewElement = this.previewSpanTargets[index];
    if (!previewElement) return;

    const icon = '<i class="fas fa-user checked-icon"></i>';
    const hasIcon = previewElement.innerHTML.includes(icon);
    const text = inputValue || (previewElement.getAttribute('name') || '').replace(/_/g, ' ');

    previewElement.innerHTML = hasIcon ? icon + text : text;
  }

  // MUTATE DOM

  createAndFillPreviewSpans() {
    let totalTags = 0;
  
    Array.from(this.lineWithTagTargets).forEach((el) => {
      const match = el.textContent.match(/\{(.*?)\}/);
      if (!match) return;
      const placeholder = match[1].replace(/_/g, " ");
      const autofilled = el.dataset.autofilled
      Array.from(el.querySelectorAll('.fr-deletable.fr-tribute')).forEach((span) => {
        const newNode = document.createElement('span');
        newNode.setAttribute(`data-${this.identifier}-target`, 'previewSpan');
        newNode.setAttribute('name', span.textContent.replace(/{|}/g, ""));
  
        const inputValue = this.tagValueInputTargets[totalTags].value;
        const changeColor = inputValue && autofilled == 'true'
        newNode.classList.add(changeColor ? 'tags-color-auto' : 'tags-color');
        newNode.innerHTML = inputValue ? inputValue : placeholder;
  
        span.parentNode.replaceChild(newNode, span);
        totalTags++;
      });
    });
  }

  filledByCandidate(e) {
    const checkbox = e.target;
    const tagKey = checkbox.dataset.tagKey;

    this.markLineFilledByCandidate(checkbox.closest('.js-has-tag'), checkbox.checked);

    /* Every occurrence of a tag is bound to the same merge field row, so the flag applies to all
       of them. Keep the other checkboxes and their icons in step with the one just clicked. */
    if (!tagKey || !this.hasFilledByCandidateInputTarget) return;

    this.filledByCandidateInputTargets.forEach((sibling) => {
      if (sibling === checkbox || sibling.dataset.tagKey !== tagKey) return;

      sibling.checked = checkbox.checked;
      this.markLineFilledByCandidate(sibling.closest('.js-has-tag'), checkbox.checked);
    });
  }

  markLineFilledByCandidate(lineWithTag, checked) {
    if (!lineWithTag) return;

    let span = lineWithTag.querySelector('.tags-color');
    let label = lineWithTag.querySelector('.tags');

    if (!span) {
      span = lineWithTag.querySelector('.tags-color-auto');
      /* On an autofilled line the label element carries .tags-color-auto itself rather than
         wrapping it, so querySelector — which only looks at descendants — finds nothing. */
      const autofilledLabel = lineWithTag.querySelector('.tags-bg-auto');
      label = autofilledLabel && (autofilledLabel.querySelector('.tags-color-auto') || autofilledLabel);
    }
    if (!span) return;

    if (checked) {
      if (span.querySelector('.checked-icon')) return;

      span.insertBefore(this.userIcon('checked-icon'), span.firstChild);
      if (label) label.insertBefore(this.userIcon('checked-icon2'), label.firstChild);
    } else {
      const existingIcon = lineWithTag.querySelector('.checked-icon');
      if (existingIcon) existingIcon.remove();
      const labelIcon = label && label.querySelector('.checked-icon2');
      if (labelIcon) labelIcon.remove();
    }
  }

  userIcon(extraClass) {
    const icon = document.createElement('i');
    icon.classList.add('fas', 'fa-user', extraClass);

    return icon;
  }

  showField() {
    this.hideAllFields();
    if (this.indexValue < 0) return;
    if (this.floaterTargets[this.indexValue] === undefined) return;

    this.floaterTargets[this.indexValue].classList.remove(this.displayNoneClass);
    this.lineWithTagTargets[this.indexValue].classList.add(this.activeClass);
    this.floaterTargets[this.indexValue].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hideAllFields() {
    Array.from(this.floaterTargets).forEach((el, i) => {
      el.classList.add(this.displayNoneClass);
      this.lineWithTagTargets[i].classList.remove(this.activeClass);
    });
  }
}
